import { useEffect, useRef } from "react";
import type { PaperWithRole } from "@/types/paper";
import { useReadingStatus } from "./useReadingStatus";

/** Reading-state automation (spec 2026-06-07, amended 2026-09-27): client
 * decides WHEN, server decides WHETHER. Reading status is an engagement
 * ladder only CONTENT can move — selecting a paper / reading its abstract
 * never changes it (the old 7s detail-pane dwell skim is gone; don't re-add).
 *
 * Skim: content (PDF or HTML full text) open for this paper with >= 15s
 *   active time OR >= 2 distinct PDF pages seen.
 * Read (PDF only): distinct pages >= min(3, numPages) AND >= 90s active PDF
 *   time. HTML full text counts toward skim only.
 * Active time accrues only while content is open and the tab is visible.
 * Each fires at most once per paper per session. Server re-checks every
 * guardrail; the eligibility pre-check here only saves pointless requests. */
const RANK: Record<string, number> = { unread: 0, skimmed: 1, read: 2 };
const SKIM_ACTIVE_MS = 15_000;
const SKIM_MIN_PAGES = 2;
const READ_MIN_PAGES = 3;
const READ_MIN_ACTIVE_MS = 90_000;
const TICK_MS = 1_000;

function isDismissed(p: PaperWithRole): boolean {
  return (
    !!p.exclusion_reason ||
    (p.user_score_override != null && p.user_score_override <= 30)
  );
}

function eligible(p: PaperWithRole | null, target: "skimmed" | "read"): boolean {
  if (!p) return false;
  if (p.reading_status_source === "manual") return false;
  if ((RANK[target] ?? -1) <= (RANK[p.reading_status] ?? 0)) return false;
  return !isDismissed(p);
}

export interface ViewerEngagementInput {
  /** Paper whose content (PDF or full text) is open; null when none. */
  paperId: string | null;
  /** Current 1-based page in the PDF viewer. */
  page: number;
  /** True while the Curate PDF viewer (inline or maximized) is showing. */
  pdfOpen: boolean;
  /** Page count of the open PDF, once loaded. */
  numPages?: number | null;
  /** True while the HTML full-text viewer is showing paperId's text. */
  fullTextOpen?: boolean;
}

export function useReadingAutopromote(
  conceptId: string,
  selectedPaper: PaperWithRole | null,
  viewer: ViewerEngagementInput,
) {
  const mutation = useReadingStatus(conceptId);
  const mutateRef = useRef(mutation.mutate);
  mutateRef.current = mutation.mutate;
  // Latest selected paper for eligibility checks at fire time (the cache
  // sync may have updated status/source since the timer started).
  const selectedRef = useRef(selectedPaper);
  selectedRef.current = selectedPaper;
  const numPagesRef = useRef(viewer.numPages);
  numPagesRef.current = viewer.numPages;

  const pagesSeen = useRef(new Map<string, Set<number>>());
  const activeMs = useRef(new Map<string, number>());
  const pdfActiveMs = useRef(new Map<string, number>());
  const skimFired = useRef(new Set<string>());
  const readFired = useRef(new Set<string>());

  const pdfOpen = viewer.pdfOpen && !!viewer.paperId;
  const contentOpen = !!viewer.paperId && (viewer.pdfOpen || !!viewer.fullTextOpen);

  useEffect(() => {
    if (!pdfOpen || !viewer.paperId) return;
    const set = pagesSeen.current.get(viewer.paperId) ?? new Set<number>();
    set.add(viewer.page);
    pagesSeen.current.set(viewer.paperId, set);
  }, [pdfOpen, viewer.paperId, viewer.page]);

  useEffect(() => {
    if (!contentOpen || !viewer.paperId) return;
    const id = viewer.paperId;
    const interval = setInterval(() => {
      if (document.hidden) return; // hidden tab: clock paused
      activeMs.current.set(id, (activeMs.current.get(id) ?? 0) + TICK_MS);
      if (pdfOpen) {
        pdfActiveMs.current.set(id, (pdfActiveMs.current.get(id) ?? 0) + TICK_MS);
      }
      const cur = selectedRef.current;
      if (!cur || cur.id !== id) return;
      const pages = pagesSeen.current.get(id)?.size ?? 0;

      // Read (PDF only) is checked first so a same-tick crossing fires
      // read alone; a fired read also suppresses any later skim.
      const readPages = Math.min(READ_MIN_PAGES, numPagesRef.current ?? READ_MIN_PAGES);
      if (
        pdfOpen &&
        !readFired.current.has(id) &&
        pages >= readPages &&
        (pdfActiveMs.current.get(id) ?? 0) >= READ_MIN_ACTIVE_MS &&
        eligible(cur, "read")
      ) {
        readFired.current.add(id);
        skimFired.current.add(id);
        mutateRef.current({ paperId: id, status: "read", source: "auto" });
        return;
      }

      if (
        !skimFired.current.has(id) &&
        ((activeMs.current.get(id) ?? 0) >= SKIM_ACTIVE_MS || pages >= SKIM_MIN_PAGES) &&
        eligible(cur, "skimmed")
      ) {
        skimFired.current.add(id);
        mutateRef.current({ paperId: id, status: "skimmed", source: "auto" });
      }
    }, TICK_MS);
    return () => clearInterval(interval);
  }, [contentOpen, pdfOpen, viewer.paperId]);
}
